import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideBrowserGlobalErrorListeners,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { IonicStorageModule } from '@ionic/storage-angular';
import { Drivers } from '@ionic/storage';

import {
  PreloadAllModules,
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
  withViewTransitions,
} from '@angular/router';
import { DATE_PIPE_DEFAULT_OPTIONS, registerLocaleData } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import Aura from '@primeuix/themes/aura';
import { es } from 'primelocale/js/es.js';
import localeEs from '@angular/common/locales/es-PE';
import { provideSpinnerConfig } from 'ngx-spinner';

import { IonicStorageAdapter } from '@core/services/adapters/ionic-storage.adapter';
import { STORAGE_STRATEGY } from '@core/services/storage.token';
import { JwtInterceptor } from '@core/interceptors/jwt.interceptor';
import { LoadingInterceptor } from '@core/interceptors/loading.interceptor';
import { ErrorInterceptor } from '@core/interceptors/error.interceptor';

import { APP_ROUTES } from './app.routes';
import { environment } from '../environments/environment';

registerLocaleData(localeEs, 'es');

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'es' },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { timezone: '-0500' } },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      APP_ROUTES,
      withViewTransitions(),
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
      withComponentInputBinding()
    ),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    providePrimeNG({
      translation: es,
      // darkModeSelector con una clase que nunca se agrega al DOM: fuerza modo claro
      // siempre. Sin esto, PrimeNG usa el default 'system' y aplica su tema oscuro
      // cuando el SO está en modo oscuro (p.ej. Linux), quedando texto ilegible
      // (blanco sobre el fondo claro de la app). IN-470 re-test (modo oscuro).
      theme: { preset: Aura, options: { darkModeSelector: '.app-dark' } },
    }),
    provideSpinnerConfig({ type: 'ball-clip-rotate' }),
    MessageService,
    ConfirmationService,
    DialogService,
    importProvidersFrom(
      IonicStorageModule.forRoot({
        name: '_iocoinstorage',
        driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage],
      })
    ),
    { provide: STORAGE_STRATEGY, useExisting: IonicStorageAdapter },
    // Runtime config: la URL de la API se lee de /config.json (poblado por el deploy), para
    // compilar una sola vez y configurar por ambiente. Vacío → usa el environment compilado.
    provideAppInitializer(async () => {
      try {
        const res = await fetch('config.json', { cache: 'no-store' });
        if (res.ok) {
          const cfg = (await res.json()) as { apiBaseUrl?: string };
          if (cfg.apiBaseUrl) {
            environment.api.baseUrl = cfg.apiBaseUrl;
          }
        }
      } catch {
        // Sin config.json (dev local): se usa el baseUrl del environment compilado.
      }
    }),
    provideAppInitializer(() => inject(IonicStorageAdapter).init()),
  ],
};
